import React, {createContext, useContext} from 'react';
import {Loader2} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {Button, type ButtonProps} from '@common-web/components/ui/button';
import {useTenantTheme} from '@common-web/providers/tenantThemeProvider';
import type {PageHeaderVariant} from '@common-web/types/tenantTheme';

const PageHeaderVariantContext = createContext<PageHeaderVariant>('navy');

/** Returns the resolved variant of the nearest `PageHeader` ancestor. */
export const usePageHeaderVariant = (): PageHeaderVariant => useContext(PageHeaderVariantContext);

interface PageHeaderProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  /** Action buttons / controls rendered on the right side of the header. */
  children?: React.ReactNode;
  isFetching?: boolean;
  isLoading?: boolean;
  /**
   * Overrides the tenant-configured variant for this single header. Prefer
   * configuring `TenantThemeConfig.header.variant` so a tenant stays consistent.
   */
  variant?: PageHeaderVariant;
  /** Overrides the tenant-configured `pattern` flag for the `navy` variant. */
  pattern?: boolean;
}

/**
 * Shared, tenant-customizable page header. Every portal page should use this so
 * titles, bylines, and header actions stay consistent for a given client. The
 * visual style is driven by `TenantThemeConfig.header` (see `tenantTheme.ts`),
 * with `navy` (full-bleed gradient hero) as the default and `flat` (compact
 * bordered bar) as the opt-in alternative.
 *
 * Render header buttons with `PageHeaderAction` so they pick up the correct
 * styling for the active variant automatically.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  icon,
  children,
  isFetching = false,
  isLoading = false,
  variant,
  pattern,
}) => {
  const {header} = useTenantTheme();
  const resolvedVariant: PageHeaderVariant = variant ?? header?.variant ?? 'navy';
  const showSpinner = isFetching && !isLoading;

  if (resolvedVariant === 'flat') {
    return (
      <PageHeaderVariantContext.Provider value='flat'>
        <div
          className={cn(
            'flex items-center justify-between gap-4 px-6 py-4 border-b bg-white',
            header?.className,
          )}
        >
          <div>
            <div className='flex items-center gap-2'>
              {icon && <span className='text-primary'>{icon}</span>}
              <h1 className={cn('text-2xl font-semibold', header?.titleClassName)}>{title}</h1>
            </div>
            {description && (
              <p className={cn('text-sm text-muted-foreground', header?.descriptionClassName)}>
                {description}
              </p>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {showSpinner && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
            {children}
          </div>
        </div>
      </PageHeaderVariantContext.Provider>
    );
  }

  const showPattern = pattern ?? header?.pattern ?? true;

  return (
    <PageHeaderVariantContext.Provider value='navy'>
      <div
        className={cn(
          'relative bg-gradient-to-r from-brand-navy to-brand-navy-dark overflow-hidden',
          header?.className,
        )}
      >
        {showPattern && (
          <div className='absolute inset-0 opacity-10'>
            <div
              className='absolute inset-0'
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />
          </div>
        )}

        <div className='relative px-8 py-10'>
          <div className='flex items-center justify-between max-w-7xl mx-auto'>
            <div>
              <div className='flex items-center gap-3 mb-3'>
                {icon && (
                  <div className='p-2 bg-white/10 rounded-lg backdrop-blur-sm text-white'>
                    {icon}
                  </div>
                )}
                <h1 className={cn('text-4xl font-bold text-white', header?.titleClassName)}>
                  {title}
                </h1>
              </div>
              {description && (
                <p
                  className={cn(
                    'text-brand-gray-light text-lg max-w-2xl',
                    header?.descriptionClassName,
                  )}
                >
                  {description}
                </p>
              )}
            </div>
            <div className='flex items-center gap-3'>
              {showSpinner && (
                <div className='bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20'>
                  <Loader2 className='h-5 w-5 animate-spin text-white' />
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    </PageHeaderVariantContext.Provider>
  );
};

type PageHeaderActionTone = 'primary' | 'secondary' | 'destructive';

export interface PageHeaderActionProps extends ButtonProps {
  /**
   * Visual weight of the action. `primary` is the main CTA, `secondary` is a
   * supporting action. The concrete colors are resolved from the active
   * `PageHeader` variant so the button is legible on both the navy and flat
   * headers.
   */
  tone?: PageHeaderActionTone;
}

const NAVY_TONE_CLASSES: Record<'primary' | 'secondary', string> = {
  primary: 'bg-white text-brand-navy hover:bg-gray-50 font-semibold shadow-md hover:shadow-lg',
  secondary: 'bg-white/10 border-white/20 text-white hover:bg-white/20',
};

/**
 * Button intended for the `PageHeader` action slot. It reads the active header
 * variant from context and applies legible styling for that background, so
 * callers never hardcode `bg-white` / `text-white` against a specific variant.
 */
export const PageHeaderAction = React.forwardRef<HTMLButtonElement, PageHeaderActionProps>(
  ({tone = 'primary', variant, className, ...props}, ref) => {
    const headerVariant = usePageHeaderVariant();

    if (tone === 'destructive') {
      return <Button ref={ref} variant={variant ?? 'destructive'} className={className} {...props} />;
    }

    if (headerVariant === 'navy') {
      return (
        <Button
          ref={ref}
          variant={tone === 'secondary' ? 'outline' : 'default'}
          className={cn(NAVY_TONE_CLASSES[tone], className)}
          {...props}
        />
      );
    }

    return (
      <Button
        ref={ref}
        variant={variant ?? (tone === 'secondary' ? 'outline' : 'default')}
        className={className}
        {...props}
      />
    );
  },
);
PageHeaderAction.displayName = 'PageHeaderAction';
